import { FC, useEffect, useMemo } from 'react';

import { useRecoilValue, useSetRecoilState } from 'recoil';

import {
  sellerMonthlyAccountedDataAtom,
  sellerMonthlyAccountedStatusAtom,
  sellerNeedAccountDataAtom,
  sellerNeedAccountStatusAtom,
} from '@shared/api/needAccount/sellerAccount.atom';
import {
  useSellerMonthlyAccountedHook,
  useSellerNeedAccountHook,
} from '@shared/api/needAccount/sellerAccount.hook';
import { ShopData } from '@shared/api/shop/shop.interface';
import { Loading } from '@shared/components/Loading';
import { useAuth } from '@shared/state/hooks/useAuth';
import dayjs from 'dayjs';

import {
  SELLER_BRAND_ETC_ID,
  SellerBrandOption,
  sellerAccountSelectYearAtom,
  sellerAccountUserIdAtom,
  sellerBrandOptionsAtom,
  sellerSelectedBrandIdsAtom,
} from './accountContent.atom';
import { AnnualAccountedContent } from './AnnualAccountedContent';
import { PresentAccountContent } from './PresentAccountContent';

interface Props {
  shop: ShopData;
}

export const SellerAccountContent: FC<Props> = function SellerAccountContent({
  shop,
}: Props) {
  const { isAdmin } = useAuth();

  // admin이 대리 조회 시 shop의 userId 전달 (users는 populate 여부에 따라 string | object)
  const firstUser = shop.users?.[0];
  const userId = isAdmin()
    ? typeof firstUser === 'string'
      ? firstUser
      : firstUser?._id
    : undefined;

  const setUserId = useSetRecoilState(sellerAccountUserIdAtom);
  const setBrandOptions = useSetRecoilState(sellerBrandOptionsAtom);
  const setSelectedBrandIds = useSetRecoilState(sellerSelectedBrandIdsAtom);

  // bugoBrand 옵션 목록 + 전체 선택 초기화 (etc 포함) + userId 설정
  useEffect(() => {
    if (shop) {
      setUserId(userId);
      const options: SellerBrandOption[] = [
        ...shop.bugoBrands.map((bugoBrand) => ({
          id: bugoBrand._id,
          label: bugoBrand?.bugoAgency?.nickName ?? bugoBrand._id,
        })),
        { id: SELLER_BRAND_ETC_ID, label: '기타' },
      ];
      setBrandOptions(options);
      setSelectedBrandIds(new Set(options.map((o) => o.id)));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // 연도 선택
  const selectYear = useRecoilValue(sellerAccountSelectYearAtom);
  const selectedYear = dayjs(selectYear).toDate().getFullYear();

  // ── 새 API hook 사용 (admin이면 userId 전달) ──
  useSellerNeedAccountHook(userId);
  const needAccountStatus = useRecoilValue(sellerNeedAccountStatusAtom);
  const needAccountData = useRecoilValue(sellerNeedAccountDataAtom);

  useSellerMonthlyAccountedHook(selectedYear, userId);
  const monthlyAccountedStatus = useRecoilValue(sellerMonthlyAccountedStatusAtom);
  const monthlyAccountedData = useRecoilValue(sellerMonthlyAccountedDataAtom);

  // 정산예정내역
  const needAccountMonthly = needAccountData?.monthly ?? null;
  const needAccountLoading = needAccountStatus !== 'success';

  // 정산완료내역
  const hasAccountedData = !!monthlyAccountedData;
  const accountedRefetching = monthlyAccountedStatus !== 'success';

  const presentAccount = useMemo(() => {
    if (needAccountMonthly) {
      return (
        <div className="relative">
          {needAccountLoading && (
            <div className="absolute inset-0 z-10 flex items-center justify-center bg-white/60">
              <Loading />
            </div>
          )}
          <PresentAccountContent needAccountMonthly={needAccountMonthly} />
        </div>
      );
    } else {
      return <Loading text="정산예정내역 로딩중" />;
    }
  }, [needAccountMonthly, needAccountLoading]);

  const annualContent = useMemo(() => {
    if (hasAccountedData) {
      return (
        <div className="relative">
          {accountedRefetching && (
            <div className="absolute inset-0 z-10 flex items-center justify-center bg-white/60">
              <Loading />
            </div>
          )}
          <AnnualAccountedContent
            monthlyAccounted={monthlyAccountedData.monthly}
            selectedYear={selectedYear}
          />
        </div>
      );
    } else {
      return <Loading text="정산완료내역 로딩중" />;
    }
  }, [hasAccountedData, accountedRefetching, monthlyAccountedData, selectedYear]);

  return (
    <div className="space-y-4 py-4">
      {presentAccount}
      {annualContent}
    </div>
  );
};
